/**
 * TreeExportService
 *
 * Service for exporting family tree visualizations to various formats
 * Supports SVG, PNG, and JPEG exports with customizable options
 */

import { TreeExportOptions, TreeExportFormat, DEFAULT_EXPORT_OPTIONS } from '../types';

export class TreeExportService {
  /**
   * Export tree to specified format
   *
   * @param svgElement - SVG element to export
   * @param options - Export configuration
   * @throws Error if export fails
   */
  static async exportTree(
    svgElement: SVGSVGElement,
    options: Partial<TreeExportOptions> = {}
  ): Promise<void> {
    const fullOptions: TreeExportOptions = {
      ...DEFAULT_EXPORT_OPTIONS,
      ...options,
    };

    try {
      switch (fullOptions.format) {
        case 'svg':
          await this.exportToSVG(svgElement, fullOptions);
          break;
        case 'png':
          await this.exportToPNG(svgElement, fullOptions);
          break;
        case 'jpeg':
          await this.exportToJPEG(svgElement, fullOptions);
          break;
        default:
          throw new Error(`Unsupported export format: ${fullOptions.format}`);
      }
    } catch (error: any) {
      throw new Error(`Failed to export tree: ${error.message}`);
    }
  }

  /**
   * Export tree as SVG file
   *
   * @param svgElement - SVG element to export
   * @param options - Export options
   */
  static async exportToSVG(
    svgElement: SVGSVGElement,
    options: TreeExportOptions
  ): Promise<void> {
    try {
      // Clone the SVG element to avoid modifying the original
      const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;

      // Prepare SVG for export
      const preparedSvg = this.prepareSVGForExport(clonedSvg, options);

      // Serialize to string
      const serializer = new XMLSerializer();
      let svgString = serializer.serializeToString(preparedSvg);

      // Add XML declaration
      svgString = '<?xml version="1.0" encoding="UTF-8"?>\n' + svgString;

      // Create blob and download
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      this.downloadBlob(blob, `${options.fileName}.svg`);
    } catch (error: any) {
      throw new Error(`Failed to export SVG: ${error.message}`);
    }
  }

  /**
   * Export tree as PNG file
   *
   * @param svgElement - SVG element to export
   * @param options - Export options
   */
  static async exportToPNG(
    svgElement: SVGSVGElement,
    options: TreeExportOptions
  ): Promise<void> {
    try {
      const canvas = await this.rasterizeSVG(svgElement, options, 'image/png');
      const blob = await this.canvasToBlob(canvas, 'image/png', options.quality);
      this.downloadBlob(blob, `${options.fileName}.png`);
    } catch (error: any) {
      throw new Error(`Failed to export PNG: ${error.message}`);
    }
  }

  /**
   * Export tree as JPEG file
   *
   * @param svgElement - SVG element to export
   * @param options - Export options
   */
  static async exportToJPEG(
    svgElement: SVGSVGElement,
    options: TreeExportOptions
  ): Promise<void> {
    try {
      const canvas = await this.rasterizeSVG(svgElement, options, 'image/jpeg');
      const blob = await this.canvasToBlob(canvas, 'image/jpeg', options.quality || 0.92);
      this.downloadBlob(blob, `${options.fileName}.jpg`);
    } catch (error: any) {
      throw new Error(`Failed to export JPEG: ${error.message}`);
    }
  }

  // ===== Private Helper Methods =====

  /**
   * Prepare SVG for export by applying styles and cleaning up
   */
  private static prepareSVGForExport(
    svg: SVGSVGElement,
    options: TreeExportOptions
  ): SVGSVGElement {
    // Set background color if specified
    if (options.backgroundColor) {
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('width', '100%');
      rect.setAttribute('height', '100%');
      rect.setAttribute('fill', options.backgroundColor);
      svg.insertBefore(rect, svg.firstChild);
    }

    // Embed stylesheets if requested
    if (options.embedFonts) {
      this.embedStyles(svg);
    }

    // Ensure proper viewBox
    if (!svg.getAttribute('viewBox')) {
      const bbox = svg.getBBox();
      svg.setAttribute('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
    }

    // Ensure proper xmlns
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

    return svg;
  }

  /**
   * Embed external stylesheets into SVG
   */
  private static embedStyles(svg: SVGSVGElement): void {
    const styleSheets = document.styleSheets;
    let cssText = '';

    // Collect all relevant CSS rules
    for (let i = 0; i < styleSheets.length; i++) {
      try {
        const sheet = styleSheets[i];
        if (sheet.cssRules) {
          for (let j = 0; j < sheet.cssRules.length; j++) {
            cssText += sheet.cssRules[j].cssText + '\n';
          }
        }
      } catch (e) {
        // Cross-origin stylesheets will throw, skip them
        console.warn('Could not access stylesheet:', styleSheets[i].href);
      }
    }

    // Create style element
    if (cssText) {
      const styleElement = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      styleElement.setAttribute('type', 'text/css');
      styleElement.textContent = cssText;
      svg.insertBefore(styleElement, svg.firstChild);
    }
  }

  /**
   * Convert SVG to raster image using Canvas
   */
  private static async rasterizeSVG(
    svgElement: SVGSVGElement,
    options: TreeExportOptions,
    mimeType: string
  ): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
      try {
        // Clone and prepare SVG
        const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
        const preparedSvg = this.prepareSVGForExport(clonedSvg, options);

        // Get SVG dimensions
        const bbox = preparedSvg.getBBox();
        const viewBox = preparedSvg.viewBox.baseVal;
        const width = viewBox.width || bbox.width;
        const height = viewBox.height || bbox.height;

        // Apply scale
        const scale = options.scale || 2;
        const scaledWidth = width * scale;
        const scaledHeight = height * scale;

        // Serialize SVG
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(preparedSvg);

        // Create blob URL
        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);

        // Create image
        const img = new Image();
        img.width = scaledWidth;
        img.height = scaledHeight;

        img.onload = () => {
          try {
            // Create canvas
            const canvas = document.createElement('canvas');
            canvas.width = scaledWidth;
            canvas.height = scaledHeight;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
              throw new Error('Failed to get canvas context');
            }

            // Fill background if specified
            if (options.backgroundColor) {
              ctx.fillStyle = options.backgroundColor;
              ctx.fillRect(0, 0, scaledWidth, scaledHeight);
            }

            // Draw image
            ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);

            // Clean up
            URL.revokeObjectURL(url);

            resolve(canvas);
          } catch (error) {
            URL.revokeObjectURL(url);
            reject(error);
          }
        };

        img.onerror = (error) => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load SVG image'));
        };

        img.src = url;
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Convert canvas to blob
   */
  private static canvasToBlob(
    canvas: HTMLCanvasElement,
    mimeType: string,
    quality?: number
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to convert canvas to blob'));
          }
        },
        mimeType,
        quality
      );
    });
  }

  /**
   * Download blob as file
   */
  private static downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Get supported export formats
   */
  static getSupportedFormats(): TreeExportFormat[] {
    return ['svg', 'png', 'jpeg'];
  }

  /**
   * Check if a format is supported
   */
  static isFormatSupported(format: TreeExportFormat): boolean {
    return this.getSupportedFormats().includes(format);
  }

  /**
   * Get recommended export options for format
   */
  static getRecommendedOptions(format: TreeExportFormat): Partial<TreeExportOptions> {
    const base: Partial<TreeExportOptions> = {
      format,
      includeMargins: true,
      embedFonts: true,
    };

    switch (format) {
      case 'svg':
        return {
          ...base,
          backgroundColor: undefined, // Transparent by default
        };
      case 'png':
        return {
          ...base,
          quality: 1,
          scale: 2,
          backgroundColor: '#ffffff',
        };
      case 'jpeg':
        return {
          ...base,
          quality: 0.92,
          scale: 2,
          backgroundColor: '#ffffff', // JPEG doesn't support transparency
        };
      default:
        return base;
    }
  }

  /**
   * Estimate file size for export
   * Returns approximate size in KB
   */
  static estimateFileSize(
    svgElement: SVGSVGElement,
    format: TreeExportFormat,
    options: Partial<TreeExportOptions> = {}
  ): number {
    const fullOptions = { ...DEFAULT_EXPORT_OPTIONS, ...options };

    // Get SVG dimensions
    const bbox = svgElement.getBBox();
    const width = bbox.width * (fullOptions.scale || 2);
    const height = bbox.height * (fullOptions.scale || 2);

    // Rough estimates based on format
    switch (format) {
      case 'svg':
        // SVG is text-based, roughly 1KB per 100 elements
        const elementCount = svgElement.querySelectorAll('*').length;
        return elementCount * 10; // ~10 bytes per element on average
      case 'png':
        // PNG is compressed, roughly 50% of raw pixel data
        return (width * height * 4) / 2 / 1024; // 4 bytes per pixel, 50% compression
      case 'jpeg':
        // JPEG has better compression, roughly 10-20% of raw pixel data
        const quality = fullOptions.quality || 0.92;
        return (width * height * 4) * (quality * 0.2) / 1024;
      default:
        return 0;
    }
  }
}
